import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Box } from "@mui/material";
import { EraserIcon } from "lucide-react";
import styles from "./dropdown.module.css";
import DragIcon from '../../../assets/images/drag-icon.svg';

const ALL_VALUE = "__all__";
const DEFAULT_VALUE = [];
const PAGE_SIZE = 20;
const LOAD_MORE_THRESHOLD_PX = 80;

const optionValueType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

const optionShape = PropTypes.oneOfType([
    optionValueType,
    PropTypes.shape({
        label: PropTypes.node,
        value: optionValueType,
    }),
]);

const optionPropType = PropTypes.shape({
    label: PropTypes.node,
    value: optionValueType,
});

const normalizeOption = (option) => {
    if (typeof option === "object" && option !== null) {
        return {
            label: option.label ?? option.value,
            value: option.value ?? option.label,
        };
    }

    return {
        label: option,
        value: option,
    };
};

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const handleLabelHover = (el) => {
    if (!el) return;
    if (el.scrollWidth > el.clientWidth) {
        el.setAttribute("title", el.textContent || "");
    } else if (el.hasAttribute("title")) {
        el.removeAttribute("title");
    }
};

const getCheckboxClassName = (singleSelect, isChecked) => {
    const classes = [styles.checkbox];

    if (singleSelect) {
        classes.push(styles.radio);
    }

    if (isChecked) {
        classes.push(singleSelect ? styles.radioChecked : styles.checkboxChecked);
    }

    return classes.join(" ");
};

const handleDragHandleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
        event.currentTarget.focus();
    }
};

function OptionCheckbox({ option, isChecked, singleSelect, onToggle }) {
    const labelRef = useRef(null);
    const handleClick = () => onToggle(option.value);
    const handleHover = () => handleLabelHover(labelRef.current);

    return (
        <button
            className={styles.optionButton}
            type="button"
            onClick={handleClick}
            onMouseEnter={handleHover}
            onFocus={handleHover}
            onTouchStart={handleHover}
        >
            <span className={getCheckboxClassName(singleSelect, isChecked)} />
            <span ref={labelRef} className={styles.optionLabel}>
                {option.label}
            </span>
        </button>
    );
}

OptionCheckbox.propTypes = {
    option: optionPropType.isRequired,
    isChecked: PropTypes.bool.isRequired,
    singleSelect: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
};

OptionCheckbox.defaultProps = {
    singleSelect: false,
};

// Non-draggable row, used while the user is searching.
function SelectableOptionRow({ option, isChecked, singleSelect, onToggle }) {
    return (
        <Box className={styles.optionRow}>
            <OptionCheckbox
                option={option}
                isChecked={isChecked}
                singleSelect={singleSelect}
                onToggle={onToggle}
            />
        </Box>
    );
}

SelectableOptionRow.propTypes = {
    option: optionPropType.isRequired,
    isChecked: PropTypes.bool.isRequired,
    singleSelect: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
};

SelectableOptionRow.defaultProps = {
    singleSelect: false,
};

function DraggableOptionRow({ option, index, isChecked, singleSelect, onToggle }) {
    const renderRow = (dragProvided, snapshot) => {
        const rowClassName = snapshot.isDragging
            ? `${styles.optionRow} ${styles.dragging}`
            : styles.optionRow;

        return (
            <Box
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                className={rowClassName}
            >
                <OptionCheckbox
                    option={option}
                    isChecked={isChecked}
                    singleSelect={singleSelect}
                    onToggle={onToggle}
                />
                <button
                    className={styles.dragHandle}
                    type="button"
                    aria-label={`Drag ${option.label}`}
                    onKeyDown={handleDragHandleKeyDown}
                    {...dragProvided.dragHandleProps}
                >
                    <img src={DragIcon} alt="" style={{ width: 13, height: 5 }} />
                </button>
            </Box>
        );
    };

    return (
        <Draggable draggableId={`${option.value}`} index={index} disableInteractiveElementBlocking>
            {renderRow}
        </Draggable>
    );
}

DraggableOptionRow.propTypes = {
    option: optionPropType.isRequired,
    index: PropTypes.number.isRequired,
    isChecked: PropTypes.bool.isRequired,
    singleSelect: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
};

DraggableOptionRow.defaultProps = {
    singleSelect: false,
};

function DraggableOptionsList({ droppableId, options, selectedValues, singleSelect, onToggle, onDragEnd }) {
    const renderDroppable = (provided) => (
        <Box ref={provided.innerRef} {...provided.droppableProps}>
            {options.map((option, index) => (
                <DraggableOptionRow
                    key={`${option.value}`}
                    option={option}
                    index={index}
                    isChecked={selectedValues.includes(option.value)}
                    singleSelect={singleSelect}
                    onToggle={onToggle}
                />
            ))}
            {provided.placeholder}
        </Box>
    );

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={droppableId}>
                {renderDroppable}
            </Droppable>
        </DragDropContext>
    );
}

DraggableOptionsList.propTypes = {
    droppableId: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(optionPropType).isRequired,
    selectedValues: PropTypes.array.isRequired,
    singleSelect: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    onDragEnd: PropTypes.func.isRequired,
};

DraggableOptionsList.defaultProps = {
    singleSelect: false,
};

function Dropdown(props) {
    const {
        label,
        options,
        value,
        defaultValue,
        onChange,
        onApply,
        onOrderChange,
        width,
        placeholder,
        autoApply,
        singleSelect,
        disabled,
    } = props;

    const rootRef = useRef(null);
    const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
    const isControlled = Array.isArray(value);
    const appliedValue = isControlled ? value : defaultValue;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState(appliedValue);
    const [orderedOptions, setOrderedOptions] = useState(normalizedOptions);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [search, setSearch] = useState("");

    useEffect(() => {
        setOrderedOptions(normalizedOptions);
        setVisibleCount(PAGE_SIZE);
    }, [normalizedOptions]);

    useEffect(() => {
        if (isOpen) {
            setVisibleCount(PAGE_SIZE);
        } else {
            setSearch("");
        }
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [search]);

    useEffect(() => {
        setSelectedValues(appliedValue);
    }, [appliedValue]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const optionValues = useMemo(
        () => orderedOptions.map((option) => option.value),
        [orderedOptions]
    );
    const isAllSelected = optionValues.length > 0
        && optionValues.every((optionValue) => selectedValues.includes(optionValue));

    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return orderedOptions;
        return orderedOptions.filter((option) =>
            String(option.label ?? "").toLowerCase().includes(query)
        );
    }, [orderedOptions, search]);
    const isSearching = search.trim().length > 0;

    const visibleOptions = useMemo(
        () => filteredOptions.slice(0, visibleCount),
        [filteredOptions, visibleCount]
    );

    const handleMenuScroll = useCallback((event) => {
        const el = event.currentTarget;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

        if (distanceFromBottom >= LOAD_MORE_THRESHOLD_PX) {
            return;
        }

        setVisibleCount((current) => {
            if (current >= filteredOptions.length) {
                return current;
            }
            return Math.min(current + PAGE_SIZE, filteredOptions.length);
        });
    }, [filteredOptions.length]);

    const selectedLabel = useMemo(() => {
        if (selectedValues.length === 0) {
            return placeholder;
        }

        if (isAllSelected) {
            return "All";
        }

        const labelByValue = new Map(
            normalizedOptions.map((option) => [option.value, option.label])
        );
        return selectedValues
            .map((selectedValue) => labelByValue.get(selectedValue) ?? selectedValue)
            .join(", ");
    }, [placeholder, selectedValues, normalizedOptions, isAllSelected]);

    const commitSelection = (nextValue, nextOrder = orderedOptions) => {
        setSelectedValues(nextValue);
        onChange?.(nextValue, nextOrder);
        if (autoApply) {
            onApply?.(nextValue, nextOrder);
        }
    };

    const handleToggle = (optionValue) => {
        if (optionValue === ALL_VALUE) {
            const allSelected = optionValues.length > 0
                && optionValues.every((valueItem) => selectedValues.includes(valueItem));
            const nextValues = allSelected ? [] : optionValues;
            commitSelection(nextValues);
            return;
        }

        if (singleSelect) {
            const nextValues = selectedValues.includes(optionValue) ? [] : [optionValue];
            commitSelection(nextValues);
            return;
        }

        let nextValues;
        if (selectedValues.includes(optionValue)) {
            nextValues = selectedValues.filter((item) => item !== optionValue);
        } else {
            nextValues = [...selectedValues, optionValue];
        }
        commitSelection(nextValues);
    };

    const handleClearAll = () => {
        commitSelection([]);
        onApply?.([], orderedOptions);
    };

    const handleClearSelection = (event) => {
        event.stopPropagation();
        commitSelection([]);
        onApply?.([], orderedOptions);
    };

    const handleApply = () => {
        onApply?.(selectedValues, orderedOptions);
        setIsOpen(false);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const nextOptions = reorder(orderedOptions, result.source.index, result.destination.index);
        setOrderedOptions(nextOptions);
        onOrderChange?.(nextOptions);
    };

    const toggleOpen = () => {
        if (disabled) return;
        setIsOpen((current) => !current);
    };

    const handleSearchChange = (event) => setSearch(event.target.value);

    const handleSelectAllClick = () => handleToggle(ALL_VALUE);

    const renderOptionsBody = () => {
        if (orderedOptions.length === 0) {
            return <Box className={styles.emptyState}>No data found</Box>;
        }

        if (visibleOptions.length === 0) {
            return <Box className={styles.emptyState}>No results found</Box>;
        }

        if (isSearching) {
            return visibleOptions.map((option) => (
                <SelectableOptionRow
                    key={`${option.value}`}
                    option={option}
                    isChecked={selectedValues.includes(option.value)}
                    singleSelect={singleSelect}
                    onToggle={handleToggle}
                />
            ));
        }

        return (
            <DraggableOptionsList
                droppableId={`${label}-options`}
                options={visibleOptions}
                selectedValues={selectedValues}
                singleSelect={singleSelect}
                onToggle={handleToggle}
                onDragEnd={handleDragEnd}
            />
        );
    };

    return (
        <Box className={styles.dropdown} ref={rootRef} sx={{ width }}>
            {label && (
                <Box className={styles.labelRow}>
                    <label className={styles.label}>{label}</label>
                    {selectedValues.length > 0 && (
                        <EraserIcon
                            className={styles.eraserIcon}
                            onClick={handleClearSelection}
                            aria-label={`Clear ${label} filter`}
                        />
                    )}
                </Box>
            )}
            <button
                className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ""}`}
                type="button"
                onClick={toggleOpen}
                aria-expanded={isOpen}
                disabled={disabled}
            >
                <span
                    className={styles.triggerText}
                    title={selectedValues.length > 0 ? selectedLabel : undefined}
                >
                    {selectedLabel}
                </span>
                <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />
            </button>

            {isOpen && (
                <Box className={styles.menu} onScroll={handleMenuScroll}>
                    <Box className={styles.searchBox}>
                        <input
                            className={styles.searchInput}
                            type="text"
                            value={search}
                            placeholder="Search..."
                            onChange={handleSearchChange}
                            autoFocus
                        />
                    </Box>

                    <Box className={styles.optionList}>
                        {!isSearching && !singleSelect && orderedOptions.length > 0 && (
                            <Box className={styles.optionRow}>
                                <button
                                    className={styles.optionButton}
                                    type="button"
                                    onClick={handleSelectAllClick}
                                >
                                    <span className={`${styles.checkbox} ${isAllSelected ? styles.checkboxChecked : ""}`} />
                                    <span className={styles.optionLabel}>Select all</span>
                                </button>
                            </Box>
                        )}
                        {renderOptionsBody()}
                    </Box>

                    {!autoApply && (
                        <Box className={styles.actions}>
                            <button className={styles.clearButton} type="button" onClick={handleClearAll}>
                                CLEAR ALL
                            </button>
                            <button className={styles.applyButton} type="button" onClick={handleApply}>
                                APPLY
                            </button>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

Dropdown.propTypes = {
    label: PropTypes.string,
    options: PropTypes.arrayOf(optionShape),
    value: PropTypes.arrayOf(optionValueType),
    defaultValue: PropTypes.arrayOf(optionValueType),
    onChange: PropTypes.func,
    onApply: PropTypes.func,
    onOrderChange: PropTypes.func,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    autoApply: PropTypes.bool,
    singleSelect: PropTypes.bool,
    disabled: PropTypes.bool,
};

Dropdown.defaultProps = {
    label: "BG Levels",
    options: [],
    value: undefined,
    defaultValue: DEFAULT_VALUE,
    onChange: undefined,
    onApply: undefined,
    onOrderChange: undefined,
    width: "100%",
    placeholder: "All",
    autoApply: false,
    singleSelect: false,
    disabled: false,
};

export default Dropdown;